import { urlParamKeys } from './constants';

class UrlParams {
  // paypal
  public getPaypalToken() {
    return this.getParam(urlParamKeys.token);
  }

  public deletePaypalToken() {
    this.deleteParam(urlParamKeys.token);
  }

  // amazon pay
  public getAmazonCheckoutSessionId() {
    return this.getParam(urlParamKeys.amazonCheckoutSessionId);
  }

  public deleteAmazonCheckoutSessionId() {
    this.deleteParam(urlParamKeys.amazonCheckoutSessionId);
  }

  // stripe
  public getStripeIntentClientSecret() {
    return this.getParam(urlParamKeys.payment_intent_client_secret);
  }

  public deleteStripeIntentClientSecret() {
    this.deleteParam(urlParamKeys.payment_intent_client_secret);
  }

  public getIfStripeTurnedOn() {
    return this.getParam(urlParamKeys.affStripe) === 'true';
  }

  public getIfStripePhase2TurnedOn() {
    return this.getParam(urlParamKeys.stripePhase2) === 'true';
  }

  public getIfStripeScheduledDowngradeTurnedOn() {
    return this.getParam(urlParamKeys.affStripeScheduledDowngrade) === 'true';
  }

  public getIfHidePromoCodesTurnedOn() {
    return this.getParam(urlParamKeys.affHidePromoCodes) === 'true';
  }

  public isSmsBundle = (): boolean => {
    return this.getParam(urlParamKeys.affSmsBundle) === 'true';
  };

  public getStripeFlagParamKey() {
    return urlParamKeys.affStripe;
  }

  public getIsRedirectFromSecureEnabled() {
    return this.getParam(urlParamKeys.affSecureRedirect) === 'true';
  }

  // order details
  public getEmailPlanType() {
    return this.getParam(urlParamKeys.planType);
  }

  public getEmailPlanId() {
    return this.getParam(urlParamKeys.emailPlanId);
  }

  public deleteEmailPlanId() {
    this.deleteParam(urlParamKeys.emailPlanId);
  }

  public getPaymentPeriod() {
    return this.getParam(urlParamKeys.paymentPeriod);
  }

  public deletePaymentPeriod() {
    this.deleteParam(urlParamKeys.paymentPeriod);
  }

  public getNonprofit() {
    return this.getParam(urlParamKeys.nonprofit);
  }

  public deleteNonprofit() {
    this.deleteParam(urlParamKeys.nonprofit);
  }

  public getAddons() {
    return this.getParam(urlParamKeys.addons);
  }

  public deleteAddons() {
    this.deleteParam(urlParamKeys.addons);
  }

  public getCurrency() {
    return this.getParam(urlParamKeys.currency);
  }

  public deleteCurrency() {
    this.deleteParam(urlParamKeys.currency);
  }

  public getContactCount() {
    return this.getParam(urlParamKeys.contactCount);
  }

  public deleteContactCount() {
    this.deleteParam(urlParamKeys.contactCount);
  }

  public getPromoCodes() {
    const promoCodes = this.getArrayParam(urlParamKeys.promoCode).concat(this.getArrayParam(urlParamKeys.promotionCd));

    return promoCodes;
  }

  public deletePromoCode(promoCode: string) {
    this.deleteParamWithValue(urlParamKeys.promoCode, promoCode);
    this.deleteParamWithValue(urlParamKeys.promotionCd, promoCode);
  }

  public getGa4Debug() {
    return this.getParam(urlParamKeys.ga4Debug);
  }

  public getReturnUrl() {
    return (
      this.getParam(urlParamKeys.returnurl) ||
      this.getParam(urlParamKeys.returnURL) ||
      this.getParam(urlParamKeys.returnUrl)
    );
  }

  public deleteReturnUrl() {
    this.deleteManyParams([urlParamKeys.returnurl, urlParamKeys.returnURL, urlParamKeys.returnUrl]);
  }

  public getReturnLabel() {
    return this.getParam(urlParamKeys.returnLabel);
  }

  public deleteReturnLabel() {
    this.deleteParam(urlParamKeys.returnLabel);
  }

  // sso
  public getGoogleSso() {
    return this.getParam(urlParamKeys.googleSso);
  }

  public getDistUiAccountId() {
    return this.parseDistUiAccountDataCookie();
  }

  // private methods
  private getParam(param: string) {
    return this.getParams().get(param);
  }

  private getArrayParam(param: string) {
    return this.getParams().getAll(param);
  }

  private deleteParam(param: string) {
    const urlObj = this.getParams();

    const prevSearchParams = urlObj.toString();

    urlObj.delete(param);

    if (urlObj.toString() !== prevSearchParams) {
      this.replaceState(urlObj);
    }
  }

  private deleteManyParams(params: string[]) {
    const urlObj = this.getParams();

    const prevSearchParams = urlObj.toString();

    params.forEach((p) => urlObj.delete(p));

    if (urlObj.toString() !== prevSearchParams) {
      this.replaceState(urlObj);
    }
  }

  private replaceState(urlObj: URLSearchParams) {
    const urlObjString = urlObj.toString();

    const searchParams = urlObjString.length > 0 ? `?${urlObjString}` : '';

    window.history.replaceState({}, '', window.location.origin + window.location.pathname + searchParams);
  }

  private deleteParamWithValue(param: string, value: string) {
    const currentSearchParams = this.getParams();
    const nextSearchParams = new URLSearchParams();
    let found = false;

    currentSearchParams.forEach((loopValue, loopParam) => {
      if (loopParam !== param || loopValue !== value) {
        nextSearchParams.append(loopParam, loopValue);
      } else {
        found = true;
      }
    });

    if (found) {
      window.history.replaceState(
        {},
        '',
        `${window.location.origin}${window.location.pathname}?${nextSearchParams.toString()}`
      );
    }
  }

  private getParams() {
    const { search } = window.location;

    return new URLSearchParams(search.slice(1));
  }

  private parseDistUiAccountDataCookie(): string | null {
    const cookiePrefix = 'distui_account_data=';
    const cookieEntry = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(cookiePrefix));

    if (!cookieEntry) return null;

    try {
      const rawValue = cookieEntry.slice(cookiePrefix.length);
      // Handle URL-encoded cookie value
      const decodedValue = decodeURIComponent(rawValue);
      // Handle quoted JSON string - remove surrounding quotes if present
      const jsonValue = decodedValue.replace(/^"(.*)"$/, '$1');
      // Handle escaped JSON strings (e.g., from JSDOM or certain cookie formats)
      const unescapedJson = jsonValue.replace(/\\"/g, '"');
      const parsed = JSON.parse(unescapedJson) as { accountId?: string | number };

      return parsed.accountId?.toString() ?? null;
    } catch {
      return null;
    }
  }
}

export const urlParams = new UrlParams();
